import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import ActivityCard from "@/components/ActivityCard";
import TrialEndingBanner from "@/components/TrialEndingBanner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Plus, Fire, Calendar, MapPin, Bicycle, PersonSimpleRun, TennisBall } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [upcoming, setUpcoming] = useState([]);
  const [popular, setPopular] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [u, p, m] = await Promise.all([
          api.get("/activities?sort=upcoming&limit=6"),
          api.get("/activities?sort=popular&limit=6"),
          // Active sessions only — completed ones live on /my-activities under the
          // "Completed" tab so the home feed stays focused on what's still ahead.
          Promise.all([
            api.get("/activities/mine?status=joined").catch(() => ({ data: [] })),
            api.get("/activities/mine?status=hosted").catch(() => ({ data: [] })),
          ]).then(([j, h]) => {
            const seen = new Set();
            const merged = [];
            for (const a of [...(h.data || []), ...(j.data || [])]) {
              if (a && !seen.has(a.activity_id)) {
                seen.add(a.activity_id);
                merged.push(a);
              }
            }
            merged.sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));
            return { data: merged };
          }),
        ]);
        setUpcoming(u.data || []);
        setPopular(p.data || []);
        setMine(m.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <TrialEndingBanner />
        <div className="grid lg:grid-cols-3 gap-6 items-end mb-12">
          <div className="lg:col-span-2 animate-fade-up">
            <div className="label-overline text-[#007AFF]">{t("dashboard.kicker")}</div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-2">
              {t("dashboard.greeting", { name: (user?.name || "ATHLETE").split(" ")[0].toUpperCase() })}
            </h1>
            <p className="text-zinc-300 mt-3 max-w-xl text-base">{t("dashboard.intro")}</p>
          </div>
          <div className="flex lg:justify-end">
            <Link to="/create" data-testid="dashboard-create-btn" className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-6 py-4 flex items-center gap-3">
              <Plus weight="bold" size={20} />
              <span className="font-display tracking-tight text-xl">{t("dashboard.create_group")}</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-12">
          <SportTile to="/explore?sport=cycling" Icon={Bicycle} color="#007AFF" label="Cycling" testId="tile-cycling" labelOverline={t("dashboard.explore_label")} />
          <SportTile to="/explore?sport=running" Icon={PersonSimpleRun} color="#FF3B30" label="Running" testId="tile-running" labelOverline={t("dashboard.explore_label")} />
          <SportTile to="/explore?sport=padel" Icon={TennisBall} color="#34C759" label="Padel" testId="tile-padel" labelOverline={t("dashboard.explore_label")} />
        </div>

        <Section icon={<Calendar weight="duotone" size={18} color="#007AFF" />} title={t("dashboard.upcoming")} cta={<Link to="/explore" className="label-overline hover:text-white">{t("dashboard.view_all")}</Link>}>
          <Grid items={upcoming} loading={loading} emptyText={t("dashboard.empty_upcoming")} />
        </Section>

        <Section icon={<Fire weight="duotone" size={18} color="#FF3B30" />} title={t("dashboard.popular")}>
          <Grid items={popular} loading={loading} emptyText={t("dashboard.empty_popular")} />
        </Section>

        {mine.length > 0 && (
          <Section
            icon={<MapPin weight="duotone" size={18} color="#34C759" />}
            title={t("dashboard.your_sessions")}
            cta={<Link to="/my-activities" className="label-overline hover:text-white">{t("dashboard.view_all")}</Link>}
          >
            <Grid items={mine} loading={false} emptyText="" />
          </Section>
        )}
      </main>
    </div>
  );
}

function SportTile({ to, Icon, color, label, testId, labelOverline }) {
  return (
    <Link to={to} data-testid={testId} className="tf-card p-5 flex items-center gap-4 group">
      <div className="w-12 h-12 flex items-center justify-center" style={{ background: `${color}1F`, border: `1px solid ${color}` }}>
        <Icon weight="duotone" size={26} color={color} />
      </div>
      <div>
        <div className="label-overline" style={{ color }}>{labelOverline}</div>
        <div className="font-display text-2xl tracking-tight">{label.toUpperCase()}</div>
      </div>
    </Link>
  );
}

function Section({ icon, title, cta, children }) {
  return (
    <section className="mb-12">
      <div className="flex items-end justify-between mb-5">
        <div className="flex items-center gap-3">
          {icon}
          <h2 className="font-display text-3xl tracking-tight">{title.toUpperCase()}</h2>
        </div>
        {cta}
      </div>
      {children}
    </section>
  );
}

function Grid({ items, loading, emptyText }) {
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {["sk1", "sk2", "sk3"].map((k) => (
          <div key={k} className="tf-card h-44 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!items?.length) {
    return <div className="border border-dashed border-[#262626] p-8 text-sm text-zinc-500">{emptyText}</div>;
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((a) => <ActivityCard key={a.activity_id} activity={a} />)}
    </div>
  );
}
